import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { formatJourneyRate, formatJourneySeconds } from "./journey-format";
import {
  getFunnelJourneyStep,
  statsJourneyStepKey,
  type JourneyCutKey,
  type JourneyQuery,
  type JourneyStepStats,
} from "./stats-api";

const SPLITS: Array<{ value: JourneyCutKey; label: string }> = [
  { value: "device", label: "Device" },
  { value: "source", label: "Source" },
  { value: "goal", label: "Goal" },
  { value: "door", label: "Door" },
  { value: "language", label: "Language" },
];

const page = (length: number) => ({ pageIndex: 0, pageSize: Math.max(length, 1), rowCount: length });

function splitColumns(): ColumnDef<JourneyStepStats["rows"][number], unknown>[] {
  return [
    { id: "cut", header: "Cut", accessorKey: "value" },
    { id: "viewed", header: "Viewed", accessorFn: (row) => row.viewed ?? "—", meta: { align: "right" } },
    { id: "completed", header: "Completed", accessorFn: (row) => row.completed ?? "—", meta: { align: "right" } },
    { id: "loss_rate", header: "Loss rate", accessorFn: (row) => formatJourneyRate(row.loss_rate), meta: { align: "right" } },
  ];
}

type ButtonRow = { button: string; all: number; by: Record<string, number> };

function buttonColumns(values: string[]): ColumnDef<ButtonRow, unknown>[] {
  return [
    { id: "button", header: "Button", accessorKey: "button" },
    { id: "all", header: "All", accessorKey: "all", meta: { align: "right" } },
    ...values.map((value) => ({
      id: `cut-${value}`,
      header: value,
      accessorFn: (row: ButtonRow) => row.by[value] ?? "—",
      meta: { align: "right" as const },
    })),
  ];
}

function ExperimentButton() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex w-fit" tabIndex={0}>
            <Button disabled>Test this step</Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>Experiments arrive in the next release</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function JourneyStepDrawer({
  slug,
  query,
  stepId,
  by,
  areaKey,
  onClose,
  onByChange,
}: {
  slug: string;
  query: JourneyQuery;
  stepId: string | null;
  by: JourneyCutKey;
  areaKey: string;
  onClose: () => void;
  onByChange: (by: JourneyCutKey) => void;
}) {
  const detail = useQuery({
    queryKey: statsJourneyStepKey(slug, query, stepId ?? "", by),
    queryFn: () => getFunnelJourneyStep(slug, query, stepId as string, by),
    enabled: stepId !== null,
  });
  const stats = detail.data?.data;
  const rows = stats?.rows ?? [];
  const buttons: ButtonRow[] = stats
    ? [
        ...stats.buttons,
        ...(stats.no_button ? [{ button: "No button pressed", all: stats.no_button.all, by: stats.no_button.by }] : []),
      ]
    : [];

  return (
    <Sheet open={stepId !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" aria-describedby="journey-step-description">
        <SheetHeader className="pr-12">
          <Button data-nonmutating variant="link" className="h-auto w-fit px-0" onClick={onClose}>
            ‹ Back to Journey
          </Button>
          <SheetTitle>{stats?.label ?? "Journey step"}</SheetTitle>
          <SheetDescription id="journey-step-description">
            {stats
              ? `Shown ${stats.all.viewed ?? "—"} times · completed ${stats.all.completed ?? "—"} · lost ${stats.all.lost ?? "—"} (${formatJourneyRate(stats.all.loss_rate)}) · ${stats.all.in_progress ?? "—"} people still in progress · median ${formatJourneySeconds(stats.all.median_seconds)} on the screen.`
              : "Step figures and button choices for this journey window."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-5 px-4 pb-6">
          {detail.isError ? (
            <Alert role="alert">
              <AlertTitle>Step figures unavailable.</AlertTitle>
              <AlertDescription>
                The funnel stats service did not answer. Nothing was changed. Try again shortly.{" "}
                <Button data-nonmutating variant="link" className="px-0" onClick={() => void detail.refetch()}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          ) : detail.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : stats ? (
            <>
              {stats.low_data && <p className="text-sm text-ks-warn">Under 30 people in this cut. The split and the buttons by cut are shown but say little yet.</p>}
              <label className="flex w-fit flex-col gap-1 text-sm text-ks-ink">
                Split by
                <Select value={by} onValueChange={(next) => onByChange(next as JourneyCutKey)}>
                  <SelectTrigger data-nonmutating aria-label="Split by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPLITS.map((split) => <SelectItem key={split.value} value={split.value}>{split.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </label>

              <DataTable
                columns={splitColumns()}
                rows={rows}
                page={page(rows.length)}
                onPageChange={() => {}}
                density="compact"
                emptyMessage="No cut rows to show."
                caption={`Journey step split by ${by}`}
              />

              <section className="flex flex-col gap-2">
                <h3 className="text-base font-bold text-ks-ink">Buttons pressed, by cut</h3>
                <div className="overflow-x-auto [&_table]:min-w-[32rem]">
                  <DataTable
                    columns={buttonColumns(rows.map((row) => row.value))}
                    rows={buttons}
                    page={page(buttons.length)}
                    onPageChange={() => {}}
                    density="compact"
                    emptyMessage="This step recorded no button choices."
                    caption={`Buttons pressed on ${stats.label}, split by ${by}`}
                  />
                </div>
              </section>

              <p className="text-sm text-muted-foreground">Completed means the person tapped the outcome button, or viewed a later screen of the flow within 24 hours (the moved-on rule).</p>
              <div className="flex flex-wrap items-center gap-3">
                <ExperimentButton />
                <Button data-nonmutating asChild variant="outline">
                  <a href={`/app/${encodeURIComponent(areaKey)}/app_journey`}>Open in App Journey viewer</a>
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
